import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchStore } from '@/store/searchStore';
import { useEditorStore } from '@/store/editorStore';
import { useIsMobile } from '@/hooks/useMediaQuery';

export function SearchBar() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpen = useSearchStore((s) => s.isOpen);
  const query = useSearchStore((s) => s.query);
  const matchingNodeIds = useSearchStore((s) => s.matchingNodeIds);
  const currentMatchIndex = useSearchStore((s) => s.currentMatchIndex);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Select and center on the current match
  useEffect(() => {
    if (matchingNodeIds.length === 0) return;
    const nodeId = matchingNodeIds[currentMatchIndex];
    if (nodeId) {
      useEditorStore.getState().selectNode(nodeId);
    }
  }, [matchingNodeIds, currentMatchIndex]);

  if (!isOpen) return null;

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      useSearchStore.getState().closeSearch();
      useEditorStore.getState().setMode('normal');
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      useSearchStore.getState().prevMatch();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      useSearchStore.getState().nextMatch();
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    useSearchStore.getState().setQuery(e.target.value);
  }

  const matchCount = matchingNodeIds.length;
  const matchLabel =
    matchCount > 0
      ? t('search.matchCount', { current: currentMatchIndex + 1, total: matchCount })
      : query.trim()
        ? t('search.noMatches')
        : '';

  return (
    <div
      style={{
        position: 'absolute',
        top: 56,
        right: isMobile ? 16 : 16,
        left: isMobile ? 16 : 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        zIndex: 50,
        boxShadow: 'var(--shadow-md)',
        fontFamily: 'var(--font-family)',
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={t('search.placeholder')}
        style={{
          background: 'var(--bg-app)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          padding: '4px 8px',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'inherit',
          outline: 'none',
          width: isMobile ? 'calc(100% - 32px)' : 200,
          flex: isMobile ? 1 : undefined,
          minWidth: 0,
        }}
      />
      {matchLabel && (
        <span
          style={{
            color: 'var(--text-secondary)',
            fontSize: 12,
            whiteSpace: 'nowrap',
          }}
        >
          {matchLabel}
        </span>
      )}
    </div>
  );
}
